import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileDown, Loader2, Plus, Trash2, Eye, EyeOff, Cloud, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Separator } from '@/components/ui/separator';
import { useTemplates } from '@/hooks/useTemplates';
import { useOCR } from '@/hooks/useOCR';
import { useDrive } from '@/hooks/useDrive';
import FormFields from '@/components/FormFields';
import OCRUpload from '@/components/OCRUpload';
import TemplateSelector from '@/components/TemplateSelector';
import DriveSetup from '@/components/DriveSetup';
import { log } from '@/lib/log';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Editor = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const clientId = searchParams.get('clientId');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [additionalDirectors, setAdditionalDirectors] = useState(2);
  const [showPreview, setShowPreview] = useState(true);
  const [currentDistricts, setCurrentDistricts] = useState([]);
  const [crmClient, setCrmClient] = useState(null);

  const { templates, fieldLabels, indianStates, districtsByState, months, daysOfWeek, relationTypes } = useTemplates();
  const { extractData, loading: ocrLoading } = useOCR();
  const { connected: driveConnected, folderConfigured, uploadToDrive, loading: driveLoading } = useDrive();

  const years = Array.from({ length: 60 }, (_, i) => new Date().getFullYear() - 50 + i);

  // Auto-fill date fields on mount
  useEffect(() => {
    const today = new Date();
    const currentMonth = months[today.getMonth()];
    const previousMonth = months[today.getMonth() - 1 < 0 ? 11 : today.getMonth() - 1];
    const daysInWords = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    setFieldValues(prev => ({
      ...prev,
      State: 'Kerala',
      CDay: daysInWords[today.getDay()],
      CMonth: currentMonth,
      CYear: today.getFullYear().toString(),
      PMonth: previousMonth,
      Date_Formal: `${today.getDate()}${getDaySuffix(today.getDate())} ${currentMonth} ${today.getFullYear()}`
    }));
  }, [months]);

  // Prefill from CRM client (?clientId=...)
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    axios.get(`${API}/crm/clients/${clientId}`).then((r) => {
      if (cancelled) return;
      const c = r.data.client;
      setCrmClient(c);
      const co = c.company || {};
      const md = c.managing_director || {};
      setClientName(co.company_name || '');
      setFieldValues((prev) => ({
        ...prev,
        CName: co.company_name || prev.CName || '',
        CIN: co.cin || prev.CIN || '',
        CAddress: co.address || prev.CAddress || '',
        State: co.state || prev.State || 'Kerala',
        BDistrict: co.district || prev.BDistrict || '',
        DateINC: co.date_of_incorporation || prev.DateINC || '',
        MD_Name: md.name || md.full_name || prev.MD_Name || '',
        MD_AAddress: md.address || prev.MD_AAddress || '',
        MD_Dob: md.date_of_birth || prev.MD_Dob || '',
        MD_Age: md.age || prev.MD_Age || '',
        MD_Aadhar: md.aadhar || prev.MD_Aadhar || '',
        MD_Pan: md.pan || prev.MD_Pan || '',
        MD_DIN: md.din || prev.MD_DIN || '',
        MD_Edu: md.education || prev.MD_Edu || '',
        MD_Uni: md.college || prev.MD_Uni || '',
        GD_YR: md.year_of_graduation || prev.GD_YR || '',
        LName: co.landlord_name || prev.LName || '',
        Landlord_Address: co.landlord_address || prev.Landlord_Address || '',
        LDOB: co.landlord_dob || prev.LDOB || '',
        LAge: co.landlord_age || prev.LAge || '',
        Rent: co.rental_amount || prev.Rent || '',
      }));
    }).catch(() => {
      toast.error('Could not load client data');
    });
    return () => { cancelled = true; };
  }, [clientId]);

  // Update districts when state changes
  useEffect(() => {
    const selectedState = fieldValues.State || 'Kerala';
    if (districtsByState[selectedState]) {
      setCurrentDistricts(districtsByState[selectedState]);
    }
    // Only the State value matters here — depending on the whole fieldValues
    // object would re-run this on every keystroke in the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues.State, districtsByState]);

  // Update preview
  useEffect(() => {
    if (selectedTemplates.length === 0 || templates.length === 0) {
      setPreviewContent('Select templates to preview...');
      return;
    }

    const selectedTemplate = templates.find(t => t.id === selectedTemplates[0]);
    if (!selectedTemplate) return;

    let filledContent = selectedTemplate.content || '';
    Object.keys(fieldValues).forEach(key => {
      const placeholder = `{${key}}`;
      const value = fieldValues[key] || `[${key}]`;
      filledContent = filledContent.split(placeholder).join(value);
    });

    setPreviewContent(filledContent);
  }, [selectedTemplates, fieldValues, templates]);

  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const calculateAge = useCallback((dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age.toString();
  }, []);

  const handleFieldChange = useCallback((field, value) => {
    setFieldValues(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate age when DOB changes
      if (field === 'MD_Dob' || field === 'LDOB') {
        const ageField = field === 'MD_Dob' ? 'MD_Age' : 'LAge';
        updated[ageField] = calculateAge(value);
      }
      if (field.includes('Dir') && field.includes('DOB')) {
        // Auto-calculate director age if needed
        const dirNum = field.match(/\d+/)?.[0];
        if (dirNum) {
          updated[`Dir${dirNum}_Age`] = calculateAge(value);
        }
      }
      
      return updated;
    });
  }, [calculateAge]);

  const handleOCRUpload = async (event, docType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const suggestions = await extractData(file, docType);
      if (suggestions) {
        setFieldValues(prev => ({ ...prev, ...suggestions }));
      }
    } catch (error) {
      // useOCR already shows a toast; log here for debugging
      log.error('[Editor] OCR upload failed:', error);
    }
  };

  const handleGenerate = async () => {
    if (!clientName.trim()) {
      toast.error('Please enter client name');
      return;
    }
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one template');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate`, {
        client_name: clientName,
        template_ids: selectedTemplates,
        field_values: fieldValues,
        client_id: clientId || undefined,
      });

      if (response.data.success) {
        toast.success(`✓ Generated ${response.data.generated_files.length} documents`);
        
        for (const file of response.data.generated_files) {
          try {
            const downloadResponse = await axios.get(`${API}/download/${file.filename}`, {
              responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', file.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (downloadError) {
            toast.error(`Failed to download ${file.template_name}`);
          }
        }
      }
    } catch (error) {
      toast.error('Failed to generate documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToDrive = async () => {
    if (!clientName.trim()) {
      toast.error('Please enter client name');
      return;
    }
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one template');
      return;
    }
    if (!driveConnected) {
      toast.error('Please connect Google Drive first');
      return;
    }
    if (!folderConfigured) {
      toast.error('Please configure Drive folder first');
      return;
    }

    try {
      await uploadToDrive(clientName, selectedTemplates, fieldValues);
    } catch (error) {
      // useDrive already shows a toast; log here for debugging
      log.error('[Editor] Drive upload failed:', error);
    }
  };

  const toggleTemplateSelection = (templateId) => {
    if (selectedTemplates.includes(templateId)) {
      setSelectedTemplates(selectedTemplates.filter(id => id !== templateId));
    } else {
      setSelectedTemplates([...selectedTemplates, templateId]);
    }
  };

  const ocrButtons = [
    { label: 'MD Networth', type: 'md_networth', id: 'md-networth' },
    { label: 'Company Networth', type: 'company_networth', id: 'company-networth' },
    { label: 'Rental Agreement', type: 'rental_agreement', id: 'rental' },
    { label: 'MD Aadhar', type: 'aadhar_md', id: 'md-aadhar' },
    { label: 'Director Aadhar', type: 'aadhar_director', id: 'dir-aadhar' },
    { label: 'Educational Doc', type: 'educational', id: 'educational' }
  ];

  const baseFieldGroups = {
    'Company Information': ['CName', 'CIN', 'CAddress', 'State', 'BDistrict', 'DateINC', 'Biz'],
    'Date Fields': ['CDate', 'CDay', 'CMonth', 'CYear', 'PMonth', 'Date_Formal'],
    'Managing Director': ['MD_Name', 'MD_Relation', 'MD_AAddress', 'MD_Dob', 'MD_Age', 'MD_Aadhar', 'MD_Pan', 'MD_DIN', 'MD_Edu', 'MD_Uni', 'GD_YR', 'MD_Biz_Act'],
    'Landlord Information': ['LName', 'LAge', 'Landlord_Address', 'LDOB'],
    'Additional Details': ['Rent', 'Countries']
  };

  const directorFieldGroups = {};
  for (let i = 2; i <= additionalDirectors; i++) {
    directorFieldGroups[`Director ${i} Information`] = [
      `Dir${i}_Name`, `Dir${i}_Address`, `Dir${i}_DOB`, `Dir${i}_DIN`, 
      `Dir${i}_Pan`, `Dir${i}_Aadhar`, `Dir${i}_Biz_Act`
    ];
  }

  const allFieldGroups = { ...baseFieldGroups, ...directorFieldGroups };

  return (
    <div className="h-full flex bg-gray-50" data-testid="editor-page">
      {/* Left Panel - Form */}
      <div className={`${showPreview ? 'w-1/2' : 'w-full'} bg-white overflow-auto transition-all duration-300`}>
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {crmClient && (
                <button
                  data-testid="back-to-client-btn"
                  onClick={() => navigate(`/clients/${clientId}`)}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Document Generator
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {crmClient
                    ? <>Generating for <span className="font-medium text-gray-700">{crmClient.company?.company_name}</span></>
                    : 'Fill in the details below to generate RA license documents'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
              data-testid="toggle-preview-btn"
              className="text-gray-600 hover:text-gray-900"
            >
              {showPreview ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Hide Preview
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Show Preview
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="px-8 py-6 space-y-8">
          {/* OCR Section */}
          <OCRUpload ocrButtons={ocrButtons} onUpload={handleOCRUpload} loading={ocrLoading} />

          {/* Client Name */}
          <div>
            <Label htmlFor="client-name" className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              Client Name
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="client-name"
              data-testid="client-name-input"
              placeholder="Enter the client's full name or company name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="h-11"
            />
          </div>

          <Separator />

          {/* Template Selection */}
          <TemplateSelector 
            templates={templates} 
            selectedTemplates={selectedTemplates} 
            onToggle={toggleTemplateSelection}
          />

          <Separator />

          {/* Director Management */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label className="text-sm font-semibold text-gray-900">Additional Directors/Partners</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Currently showing fields for {additionalDirectors} directors (Director 2 to {additionalDirectors})
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAdditionalDirectors(Math.min(additionalDirectors + 1, 4))}
                  disabled={additionalDirectors >= 4}
                  data-testid="add-director-btn"
                  className="h-8"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAdditionalDirectors(Math.max(additionalDirectors - 1, 2))}
                  disabled={additionalDirectors <= 2}
                  data-testid="remove-director-btn"
                  className="h-8"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          </div>

          {/* Field Groups */}
          <FormFields
            fieldGroups={allFieldGroups}
            fieldValues={fieldValues}
            fieldLabels={fieldLabels}
            currentDistricts={currentDistricts}
            indianStates={indianStates}
            months={months}
            daysOfWeek={daysOfWeek}
            relationTypes={relationTypes}
            years={years}
            onFieldChange={handleFieldChange}
          />
        </div>

        {/* Generate Button - Sticky */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-8 py-4 shadow-lg">
          <div className="flex items-center gap-3 mb-3">
            <DriveSetup />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button
              data-testid="generate-btn"
              onClick={handleGenerate}
              disabled={loading}
              variant="outline"
              className="h-12 rounded-xl font-medium"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileDown className="w-5 h-5 mr-2" />
                  Download
                </>
              )}
            </Button>
            <Button
              data-testid="save-drive-btn"
              onClick={handleSaveToDrive}
              disabled={driveLoading || loading || !driveConnected}
              className="h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium"
            >
              {driveLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Cloud className="w-5 h-5 mr-2" />
                  Save to Drive
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Right Panel - Preview */}
      {showPreview && (
        <div className="w-1/2 bg-gray-50 p-8 overflow-auto border-l border-gray-200">
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-1">Live Preview</h3>
            <p className="text-sm text-gray-500">
              Document preview with your filled information
            </p>
          </div>
          <div className="paper-preview shadow-xl" data-testid="preview-area">
            {previewContent}
          </div>
        </div>
      )}
    </div>
  );
};

export default Editor;
